import React, { useEffect, useMemo, useState, useRef} from "react";
import { scrollToTop } from "./landing/wizard/wizardScroll";
import { Link, useOutletContext } from "react-router-dom";
import { getAllGame } from "../../api/user";
import { SectionError } from "../../components/Skeleton/LoadError";
import PublicLeagueRow from "./landing/PublicLeagueRow";
import LeagueEmptyState, { EmptyStateArt } from "./landing/LeagueEmptyState";
import { getLeagueStatus } from "./landing/leagueStatus";
import { MdOutlineFlag } from "react-icons/md";
import usePullToRefresh from "../../hooks/usePullToRefresh";
import PullToRefreshIndicator from "../../components/Other/PullToRefreshIndicator";

// Public Leagues — every open league, in one list.
//
// Reached from "View all" beside Public Leagues on League Play, which shows only
// the first couple. So this screen's job is browsing: three phases as tabs, and
// rows rather than tiles, because comparing leagues means reading down a column
// of titles.
//
// Each tab is a real state with its own empty copy — a phase with nothing in it
// says what happens next instead of leaving a blank page.
const TABS = [
  { key: "soon", label: "Starting soon" },
  { key: "live", label: "Live now" },
  { key: "done", label: "Completed" },
];

const EMPTY = {
  soon: {
    title: "Nothing on deck",
    body: "New competitions drop regularly. Be ready.",
  },
  live: {
    title: "Nothing live right now",
    body: "Check Starting soon to see what's coming.",
  },
  done: {
    title: "No finished leagues yet",
    body: "Competitions land here once they wrap.",
  },
};

// One rule for which tab a league belongs in, so a league can't be listed as
// upcoming and drawn as if it were running.
const phaseOf = (game) => {
  const tone = getLeagueStatus(game)?.tone;
  if (game?.status === "completed" || tone === "ended") return "done";
  if (tone === "live") return "live";
  return "soon";
};

const startMs = (game) => {
  const t = game?.start_date ? new Date(game.start_date).getTime() : NaN;
  return Number.isNaN(t) ? Infinity : t;
};

const PublicLeagues = () => {
  // Contest.jsx owns the search field in its header band.
  const { search } = useOutletContext() || {};
  const [tab, setTab] = useState("soon");
  const [state, setState] = useState({ loading: true, error: false, data: [] });

  const refreshFunction = async () => {
    try {
      const res = await getAllGame();
      const all = res?.data?.data || [];
      setState({
        loading: false,
        error: false,
        data: all.filter((g) => g.game_type === "public" && !g.is_cash_prize_league),
      });
    } catch (e) {
      console.error("Public leagues refresh failed:", e);
      setState({ loading: false, error: true, data: state.data });
    }
  };


  // Client: "When you open the mobile version Cash Games > View All from League
  // Play Home, the page does not always start at the very top where the 'Cash
  // Games' title is visible... Please make sure: All three View All pages open
  // at the top of the page."
  //
  // They did not, and it is the same cause the create-league wizard had: the app
  // does not scroll the WINDOW. `.mainWrapper` is the scroller, so React Router
  // swapping the route leaves that element at whatever offset the PREVIOUS
  // screen was scrolled to — and League Play Home is usually scrolled well down
  // by the time anyone reaches a View All link. scrollToTop walks up to the real
  // scroller instead of calling window.scrollTo, which is a no-op here.
  const topRef = useRef(null);
  useEffect(() => {
    scrollToTop(topRef.current);
  }, []);

  const { containerProps, isRefreshing, pullDistance } = usePullToRefresh({
    onRefresh: refreshFunction,
    disabled: false,
  });

  useEffect(() => {
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: false }));
    getAllGame({ signal: controller.signal })
      .then((res) => {
        const all = res?.data?.data || [];
        setState({
          loading: false,
          error: false,
          // This screen is the public list: cash-prize leagues have their own
          // section on League Play and their own screen behind it.
          data: all.filter((g) => g.game_type === "public" && !g.is_cash_prize_league),
        });
      })
      .catch((e) => {
        if (e?.name === "AbortError" || e?.name === "CanceledError") return;
        console.error("Public leagues load failed:", e);
        setState({ loading: false, error: true, data: [] });
      });
    return () => controller.abort();
  }, []);

  const searched = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return state.data;
    return state.data.filter(
      (g) =>
        (g.title && g.title.toLowerCase().includes(q)) ||
        (g.description && g.description.toLowerCase().includes(q))
    );
  }, [state.data, search]);

  const buckets = useMemo(() => {
    const out = { soon: [], live: [], done: [] };
    searched.forEach((g) => out[phaseOf(g)].push(g));
    // Soonest first for what's coming; most recent first for what's finished.
    out.soon.sort((a, b) => startMs(a) - startMs(b));
    out.live.sort((a, b) => startMs(a) - startMs(b));
    out.done.sort((a, b) => startMs(b) - startMs(a));
    return out;
  }, [searched]);

  const rows = buckets[tab];

  return (
    <>
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <div className="px-4 md:px-8 pb-24 md:pb-16" {...containerProps}>
        <div ref={topRef} className="max-w-screen-md mx-auto">
        {/* Screen page title, house rule: Montserrat bold in teal. */}
        <h1 className="font-montserrat font-bold text-title-subpage md:text-title-page text-pageTitle">
          Public Leagues
        </h1>
        <p className="mt-1 text-sm md:text-base text-secondary">
          Think you can trade? Prove it.
        </p>

        {/* Underline tabs in green, as the design specifies. `greenXD` (#5DAE4D)
            is the existing token for it — the same green the reset-portfolio
            modal uses — so this follows the design without introducing a colour
            the palette doesn't already have. Teal stays the ACTIVE-NAV colour;
            these are content filters, not navigation. */}
        {/* Client Rev 4, MOBILE: "Space Starting soon / Live now / Completed
            evenly across the full tab width, one third each." — a 3-column grid,
            so each tab (and its underline) owns exactly a third whatever the
            labels say.
            DESKTOP: "Give Starting soon / Live now / Completed equal spacing
            while keeping the tab group compact and left-aligned. Set a 48px gap
            between tab items" — so the grid is dropped for a left-aligned flex
            row with gap-12 (48px). */}
        <div
          role="tablist"
          className="mt-5 grid grid-cols-3 border-b border-divider md:flex md:items-center md:gap-12"
        >
          {TABS.map((t) => {
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.key)}
                className={`-mb-px w-full border-b-2 pb-3 text-center text-sm transition-colors md:w-auto md:shrink-0 md:text-left md:text-base ${
                  active
                    ? "border-greenXD font-semibold text-greenXD"
                    : "border-transparent text-secondary hover:text-[#0F172A]"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Client Rev 4, DESKTOP: "Add one outer container card around the league
            results area below the tabs... Keep individual leagues as open rows
            inside that container, separated by thin divider lines. Do not create
            individual league cards. When a tab is empty, show the empty state
            inside the same results container."
            One container, whatever the tab is showing — rows, the empty state,
            an error or the skeleton — so the card never changes shape between
            tabs. Mobile keeps the open list the client asked for there. */}
        <div className="mt-4 md:rounded-2xl md:border md:border-gray-200 md:bg-white md:px-5">
          {state.loading ? (
            <div className="divide-y divide-divider">
              {[0, 1, 2].map((i) => (
                <div key={i} className="space-y-2 py-4">
                  <div className="h-4 w-28 animate-pulse rounded bg-[#F3F4F6]" />
                  <div className="h-5 w-3/4 animate-pulse rounded bg-[#F3F4F6]" />
                  <div className="h-4 w-1/2 animate-pulse rounded bg-[#F3F4F6]" />
                </div>
              ))}
            </div>
          ) : state.error ? (
            <div className="py-4">
              <SectionError
                message="Public leagues could not be loaded"
                onRetry={() => window.location.reload()}
              />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-2 md:py-4">
              {/* A search that matches nothing is a different situation from a
                  phase that's genuinely empty, and shouldn't claim the phase is. */}
              {search ? (
                <p className="py-10 text-center text-sm text-secondary">
                  No leagues match “{search}”.
                </p>
              ) : (
                <LeagueEmptyState
                  art={<EmptyStateArt variant="leagues" />}
                  title={EMPTY[tab].title}
                  body={EMPTY[tab].body}
                />
              )}
            </div>
          ) : (
            <div className="divide-y divide-divider">
              {rows.map((game) => (
                <PublicLeagueRow key={game.id} game={game} />
              ))}
            </div>
          )}
        </div>

        {/* The way out of browsing: if none of these fit, run your own. Below the
            list on purpose — it's the answer to having read it. */}
        <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex items-start gap-3">
            <MdOutlineFlag size={24} className="mt-0.5 shrink-0 text-[#0F4146]" />
            <div className="min-w-0">
              <h2 className="font-montserrat font-bold text-base md:text-lg text-[#0F4146]">
                Can't find the right league?
              </h2>
              {/* Client Rev 4: "The Create a Public League CTA card at the
                  bottom - subtitle wraps one word 'up'. Change the subtitle to:
                  'Build your own. Set the rules.' you can leave the desktop
                  version the way it is. It has plenty of room." */}
              <p className="mt-0.5 text-sm text-secondary">
                <span className="md:hidden">Build your own. Set the rules.</span>
                <span className="hidden md:inline">
                  Build your own. Set the rules. Open it up.
                </span>
              </p>
            </div>
          </div>
          <Link
            to="/contest/create-public-league"
            className="mt-4 flex w-full items-center justify-center rounded-xl bg-greenXD py-3.5 font-bold text-base text-white transition-colors hover:bg-[#4F9A41]"
          >
            Create a Public League
          </Link>
        </div>
      </div>
    </div>
    </>
  );
};

export default PublicLeagues;
